/**
 * LES VUES ENREGISTRÉES DES REPORTINGS — le bouton « Vues » au bout de la ligne des filtres, et sa bibliothèque.
 *
 * UTILITÉ : relancer une statistique à l'identique tient en un clic, pour toute l'équipe — période, comparaison,
 * recherche, filtres, niveaux, tri, et ce que l'onglet règle de plus. Maquette : `stvViewsVals` (popover des reportings).
 *
 * ⚠️ UNE BIBLIOTHÈQUE PAR ONGLET (décision utilisateur) ; le serveur valide et range (`sales/stats-views.ts`).
 * ⚠️ L'ONGLET DIT CE QU'UNE VUE EST : il fournit la photographie de ses réglages, leur application et leur description
 *    (`StatsViewAdapter`). Ce composant ne connaît aucun réglage — il ne ferait que recopier ceux de six onglets.
 * ⚠️ REMPLACER SE CONFIRME, et le serveur le refuse sans confirmation : un collègue peut avoir pris le nom entre-temps.
 * ⚠️ CE QUI NE SE LIT PLUS EST DIT EN APPLIQUANT (écarté au serveur, ou inconnu du catalogue de l'écran) — jamais tu.
 * ⚠️ UN COMPTE EN LECTURE APPLIQUE LES VUES DE SON MAGASIN, il n'en enregistre ni n'en supprime : les gestes ne lui sont
 *    pas offerts, et l'écran dit pourquoi.
 */
import { useEffect, useId, useState, type CSSProperties } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import { confirm, showToast } from '@orgazone/ui-web';
import { reconcileColumnLayout, type ColumnLayout } from '@orgazone/ui-tokens';
import { api, type StatsView, type StatsViewSettings, type StatsViewTab } from '../api';
import { useAuth } from '../auth';
import { t } from '../i18n';
import { StatsPopover, dimLabel, windowLabel, type LevelDim } from './stats-bar';
import type { StatsSort } from './stats-grid';
import { Trait } from './stock-views';

/** Ce qu'un onglet apporte à sa bibliothèque. */
export interface StatsViewAdapter {
  /** Les réglages affichés, tels qu'une vue les enregistre. */
  snapshot: () => StatsViewSettings;
  /** Applique une vue ; rend le nombre de réglages que l'écran n'a pas pu reprendre (inconnus de son catalogue). */
  apply: (s: StatsViewSettings) => number;
  /** La ligne qui décrit une vue sous son nom. */
  describe: (s: StatsViewSettings) => string;
  /** Ce qu'une vue reprend sur cet onglet, dit sous le champ du nom. */
  note: string;
}

/**
 * La ligne d'une vue sous son nom : ses niveaux, sa période (et sa comparaison, ou la fenêtre des réceptions), le nombre de
 * critères filtrés. Maquette : `stvViewSub`.
 */
export function describeView(s: StatsViewSettings, dims: readonly LevelDim[]): string {
  const levels = s.axes.map((k) => { const d = dims.find((x) => x.k === k); return d ? dimLabel(d) : k; }).join(' × ');
  const period = s.receptions
    ? [t('ventes {periode}', { periode: windowLabel(s.a) }), t('réceptions {periode}', { periode: windowLabel(s.receptions) })].join(' · ')
    : s.b ? t('{periode} vs {comparaison}', { periode: windowLabel(s.a), comparaison: windowLabel(s.b) }) : windowLabel(s.a);
  const n = Object.values(s.filters).filter((v) => v && v.length).length;
  return [levels, period, n ? t('{n} filtre', { n }) : ''].filter(Boolean).join(' · ');
}

/**
 * Le tri d'une vue, s'il désigne encore une colonne de l'onglet — sinon l'ordre de l'onglet, et l'écart compté.
 * `known` : les colonnes que l'onglet peut trier, `null` quand elles dépendent des données (une période, une taille).
 */
export function keptSort(sort: StatsViewSettings['sort'], axes: number, known: readonly string[] | null, fallback: StatsSort): { sort: StatsSort; lost: number } {
  if (!sort) return { sort: fallback, lost: 0 };
  const ok = sort.k.startsWith('ax:') ? Number(sort.k.slice(3)) < axes : known === null || known.includes(sort.k);
  return ok ? { sort, lost: 0 } : { sort: fallback, lost: 1 };
}

/** Les colonnes d'une vue rapprochées du catalogue de l'onglet ; celles qui n'existent plus sont comptées. */
export function keptColumns(columns: StatsViewSettings['columns'], keys: readonly string[]): { layout: ColumnLayout; lost: number } | null {
  if (!columns) return null;
  return {
    layout: reconcileColumnLayout(keys, columns),
    lost: new Set([...columns.order, ...columns.hidden].filter((k) => !keys.includes(k))).size,
  };
}

type Library =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; views: StatsView[]; unreadable: number }
  | { phase: 'failed'; message: string };

/** La largeur du menu — celle de la maquette. */
const WIDTH = 352;
/** Le nom tel que le serveur l'enregistre, et sa clé : « Point hebdo » et « point  hebdo » sont la même vue. */
const normalizeName = (s: string): string => s.normalize('NFC').replace(/\s+/g, ' ').trim();
const nameKey = (s: string): string => normalizeName(s).toLowerCase();
const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Les tracés de la maquette : le signet du bouton, la corbeille d'une ligne. */
const BOOKMARK = 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z';
const TRASH = 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6';

/**
 * Le bouton « Vues » et sa bibliothèque. `enabled` : l'onglet est ouvert sur un magasin — fermé, il ne lit rien.
 * ⚠️ RELUE À CHAQUE OUVERTURE DU MENU : un collègue a pu enregistrer ou retirer une vue depuis.
 */
export function StatsViewsButton({ tab, enabled, adapter }: { tab: StatsViewTab; enabled: boolean; adapter: StatsViewAdapter }) {
  const canWrite = useAuth().can('bo.write');
  const [lib, setLib] = useState<Library>({ phase: 'idle' });
  const [reads, setReads] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const nameId = useId();

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    // ⚠️ UNE RELECTURE NE VIDE PAS LA LISTE AFFICHÉE : elle se remplace quand la réponse arrive.
    setLib((cur) => (cur.phase === 'ready' ? cur : { phase: 'loading' }));
    api.statsViews(tab)
      .then((r) => { if (alive) setLib({ phase: 'ready', views: r.views, unreadable: r.unreadable }); })
      .catch((e: unknown) => { if (alive) setLib({ phase: 'failed', message: messageOf(e) }); });
    return () => { alive = false; };
  }, [enabled, tab, reads]);

  const reread = () => setReads((n) => n + 1);
  const close = () => setRect(null);
  const views = lib.phase === 'ready' ? lib.views : [];

  /** Appliquer : les réglages de la vue remplacent ceux de l'écran ; ce qui n'a pas pu être repris est dit. */
  const apply = (v: StatsView) => {
    const lost = v.dropped + adapter.apply(v.settings);
    close();
    showToast(lost
      ? t('Vue « {nom} » appliquée — {n} réglage n’existe plus et a été écarté', { nom: v.name, n: lost })
      : t('Vue « {nom} » appliquée', { nom: v.name }), lost ? 'warn' : 'ok');
  };

  const askReplace = (shown: string) => confirm({
    title: t('Remplacer la vue « {nom} » ?', { nom: shown }),
    message: t('Une vue porte déjà ce nom. Ses filtres seront remplacés par ceux affichés à l’écran.'),
    confirmLabel: t('Remplacer'), tone: 'info',
  });

  /** Enregistrer les réglages affichés sous un nom ; un nom déjà pris demande s'il faut remplacer. */
  const save = async () => {
    const n = normalizeName(name);
    if (!n || saving) return;
    const known = views.find((v) => nameKey(v.name) === nameKey(n));
    if (known && !(await askReplace(known.name))) return;
    setSaving(true);
    try {
      let r;
      try {
        r = await api.saveStatsView(tab, n, adapter.snapshot(), !!known);
      } catch (e) {
        // ⚠️ UN COLLÈGUE VIENT DE PRENDRE CE NOM : on demande, comme si on l'avait su.
        if (!(e instanceof ApiError && e.status === 409)) throw e;
        if (!(await askReplace(n))) return;
        r = await api.saveStatsView(tab, n, adapter.snapshot(), true);
      }
      showToast(r.replaced
        ? t('Vue « {nom} » mise à jour', { nom: r.view.name })
        : t('Vue « {nom} » enregistrée — partagée avec l’équipe', { nom: r.view.name }));
      setName('');
      close();
      reread();
    } catch (e) {
      showToast(messageOf(e), 'err');
    } finally {
      setSaving(false);
    }
  };

  /** Supprimer une vue, pour toute l'équipe — les filtres affichés ne changent pas. */
  const remove = async (v: StatsView) => {
    const ok = await confirm({
      title: t('Supprimer la vue « {nom} » ?', { nom: v.name }),
      message: t('Elle disparaît pour toute l’équipe — les vues sont partagées. Les filtres à l’écran ne changent pas.'),
      confirmLabel: t('Supprimer'), tone: 'danger',
    });
    if (!ok) return;
    try {
      const r = await api.deleteStatsView(tab, v.name);
      showToast(r.removed
        ? t('Vue « {nom} » supprimée', { nom: v.name })
        : t('La vue « {nom} » n’existait plus : elle avait déjà été retirée.', { nom: v.name }), r.removed ? 'ok' : 'info');
    } catch (e) {
      showToast(messageOf(e), 'err');
    }
    reread();
  };

  const count = lib.phase === 'ready' ? (views.length ? t('{n} vue', { n: views.length }) : t('aucune vue')) : '';
  const named = !!normalizeName(name);

  return (
    <>
      <button type="button" className="oz-stats-views" title={t('Rappeler ou enregistrer un jeu de filtres')}
        onClick={(e) => {
          if (rect) { close(); return; }
          const r = e.currentTarget.getBoundingClientRect();
          // ⚠️ ALIGNÉ SUR LE BORD DROIT DU BOUTON, à 8 px dessous (maquette) : le menu s'ouvre vers la gauche.
          setRect(new DOMRect(r.right - WIDTH, r.top, WIDTH, r.height + 2));
          setName('');
          reread();
        }}
        style={BUTTON}>
        <Trait d={BOOKMARK} size={14} stroke={2} />
        {t('Vues')}
        {count && <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>· {count}</span>}
      </button>
      {rect && (
        <StatsPopover r={rect} width={WIDTH} onClose={close}>
          <div style={HEAD}>{t('Vues enregistrées')}</div>
          <div style={{ maxHeight: 250, overflowY: 'auto', padding: '0 6px 6px' }}>
            {lib.phase === 'failed' && (
              <div role="alert" style={{ ...EMPTY, color: 'var(--rose-fg)' }}>
                {t('La liste des vues n’a pas pu être lue : {message}', { message: lib.message })}
                <button type="button" onClick={reread} style={RETRY}>{t('Réessayer')}</button>
              </div>
            )}
            {(lib.phase === 'loading' || lib.phase === 'idle') && <div style={EMPTY}>{t('Lecture des vues…')}</div>}
            {views.map((v) => (
              <div key={nameKey(v.name)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                <button type="button" className="oz-stats-view-row" onClick={() => apply(v)} style={ROW}>
                  <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', ...ELLIPSIS }}>{v.name}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1, ...ELLIPSIS }}>{adapter.describe(v.settings)}</span>
                </button>
                {canWrite && (
                  <button type="button" className="oz-stats-view-del" onClick={() => { void remove(v); }}
                    aria-label={t('Supprimer la vue « {nom} »', { nom: v.name })} title={t('Supprimer cette vue')} style={DELETE}>
                    <Trait d={TRASH} size={13} stroke={2} />
                  </button>
                )}
              </div>
            ))}
            {lib.phase === 'ready' && !views.length && (
              <div style={EMPTY}>{t('Aucune vue pour l’instant — réglez vos filtres, puis nommez-les ci-dessous.')}</div>
            )}
            {lib.phase === 'ready' && lib.unreadable > 0 && (
              <div style={{ ...EMPTY, padding: '8px 10px' }}>
                {t('{n} vue enregistrée ne se lit plus : sa période n’est plus lisible.', { n: lib.unreadable })}
              </div>
            )}
          </div>
          <div style={FOOT}>
            {canWrite ? (
              <>
                <label htmlFor={nameId} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 6 }}>
                  {t('Enregistrer les filtres affichés')}
                </label>
                <span style={{ display: 'flex', gap: 7 }}>
                  <input id={nameId} value={name} maxLength={40} className="oz-stats-view-name"
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }}
                    placeholder={t('Nom de la vue — ex. Point hebdo')} style={INPUT} />
                  <button type="button" className="oz-stats-view-save" onClick={() => { void save(); }} disabled={!named || saving}
                    style={{
                      ...SAVE, background: named ? 'var(--ink-900)' : 'var(--ink-200)', color: named ? 'var(--paper)' : 'var(--ink-400)',
                      cursor: named && !saving ? 'pointer' : 'default',
                    }}>
                    {t('Enregistrer')}
                  </button>
                </span>
                <span style={NOTE}>{adapter.note}</span>
              </>
            ) : (
              <span style={{ ...NOTE, marginTop: 0 }}>{t('Enregistrer ou supprimer une vue demande le droit de modifier le magasin.')}</span>
            )}
          </div>
        </StatsPopover>
      )}
    </>
  );
}

// ─── Les styles de la maquette ───────────────────────────────────────────────────────────────────

const ELLIPSIS: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const BUTTON: CSSProperties = {
  height: 36, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
  color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7,
  whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer',
};
const HEAD: CSSProperties = {
  padding: '10px 13px 6px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};
const ROW: CSSProperties = {
  flex: 1, minWidth: 0, display: 'block', padding: '8px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent',
  textAlign: 'left', cursor: 'pointer',
};
const DELETE: CSSProperties = {
  width: 28, height: 28, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer',
};
const EMPTY: CSSProperties = { padding: '16px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)', lineHeight: 1.5 };
const RETRY: CSSProperties = {
  display: 'block', margin: '8px auto 0', height: 28, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer',
};
const FOOT: CSSProperties = { borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', padding: '10px 12px' };
const INPUT: CSSProperties = {
  flex: 1, minWidth: 0, height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', fontSize: 'var(--t-body)', color: 'var(--ink-900)', outline: 'none',
};
const SAVE: CSSProperties = { height: 34, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap' };
const NOTE: CSSProperties = { display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 6, lineHeight: 1.5 };
